// Violet I-beam over selectable text: CSS cannot tell text from blank areas, so a class on <html> is toggled while the pointer is over text

const ACTIVE_CLASS = 'text-cursor-active';
const TOLERANCE = 1;

const textNodeAt = (x, y) => {
    let node = null;

    if (document.caretPositionFromPoint) {
        node = document.caretPositionFromPoint(x, y)?.offsetNode ?? null;
    } else if (document.caretRangeFromPoint) {
        node = document.caretRangeFromPoint(x, y)?.startContainer ?? null;
    }

    return node && node.nodeType === Node.TEXT_NODE && node.nodeValue.trim() !== '' ? node : null;
};

const pointerOverText = (node, x, y) => {
    const range = document.createRange();
    range.selectNodeContents(node);

    for (const rect of range.getClientRects()) {
        if (x >= rect.left - TOLERANCE && x <= rect.right + TOLERANCE && y >= rect.top - TOLERANCE && y <= rect.bottom + TOLERANCE) {
            return true;
        }
    }
    return false;
};

// user-select auto inherits from the parent, so walk up to the first explicit value
const isSelectable = (element) => {
    for (let node = element; node; node = node.parentElement) {
        const style = getComputedStyle(node);
        const value = style.userSelect || style.webkitUserSelect;
        if (value && value !== 'auto') return value !== 'none';
    }
    return true;
};

export default function installTextCursor() {
    const root = document.documentElement;
    let frame = null;
    let lastEvent = null;

    const update = () => {
        frame = null;
        if (!lastEvent) return;

        const { clientX: x, clientY: y } = lastEvent;
        const element = document.elementFromPoint(x, y);
        let overText = false;

        if (element) {
            const cursor = getComputedStyle(element).cursor;
            const isAuto = cursor === 'auto' || (root.classList.contains(ACTIVE_CLASS) && cursor === getComputedStyle(root).cursor);

            if (isAuto && isSelectable(element)) {
                const node = textNodeAt(x, y);
                overText = Boolean(node) && element.contains(node) && pointerOverText(node, x, y);
            }
        }

        root.classList.toggle(ACTIVE_CLASS, overText);
    };

    const onMouseMove = (event) => {
        lastEvent = event;
        if (frame === null) frame = window.requestAnimationFrame(update);
    };

    const onLeave = () => {
        lastEvent = null;
        root.classList.remove(ACTIVE_CLASS);
    };

    document.addEventListener('mousemove', onMouseMove, { passive: true });
    document.documentElement.addEventListener('mouseleave', onLeave);
    window.addEventListener('blur', onLeave);

    return () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.documentElement.removeEventListener('mouseleave', onLeave);
        window.removeEventListener('blur', onLeave);
        if (frame !== null) window.cancelAnimationFrame(frame);
        root.classList.remove(ACTIVE_CLASS);
    };
}
